import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, RefreshCw, AlertTriangle } from 'lucide-react';

interface CapacitySurface {
    teamId: string;
    window: 'now' | '1d' | '7d' | '30d';
    currentLoadPercent: number;
    predictedOverload: boolean;
    availabilityPercent: number;
    sampleSize: number;
    lastUpdated: string;
}

interface TeamCapacityDisplay {
    teamId: string;
    teamName: string;
    surface: CapacitySurface | null;
    isLoading: boolean;
    error: string | null;
}

const TEAM_IDS = ['team-frontend', 'team-backend', 'team-devops', 'team-qa'];

const getTeamDisplayName = (teamId: string): string => {
    const names: Record<string, string> = {
        'team-frontend': 'Frontend Development',
        'team-backend': 'Backend Development',
        'team-devops': 'DevOps & Infrastructure',
        'team-qa': 'Quality Assurance',
    };
    return names[teamId] || teamId;
};

export const TeamCapacityPanel: React.FC = () => {
    const [teams, setTeams] = useState<TeamCapacityDisplay[]>(
        TEAM_IDS.map(id => ({
            teamId: id,
            teamName: getTeamDisplayName(id),
            surface: null,
            isLoading: false,
            error: null,
        }))
    );
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchTeamCapacity = useCallback(async (teamId: string) => {
        setTeams(prev =>
            prev.map(t => (t.teamId === teamId ? { ...t, isLoading: true, error: null } : t))
        );

        try {
            const response = await fetch(`/api/pmo/capacity/${teamId}?window=now`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();

            setTeams(prev =>
                prev.map(t =>
                    t.teamId === teamId ? { ...t, surface: data.surface, isLoading: false } : t
                )
            );
        } catch (error) {
            console.error(`Failed to fetch capacity for ${teamId}:`, error);
            setTeams(prev =>
                prev.map(t =>
                    t.teamId === teamId ? { ...t, isLoading: false, error: 'Failed to load' } : t
                )
            );
        }
    }, []);

    const refreshTeamCapacity = useCallback(
        async (teamId: string) => {
            try {
                await fetch(`/api/pmo/capacity/${teamId}/refresh`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ windows: ['now'] }),
                });
                setTimeout(() => fetchTeamCapacity(teamId), 2000);
            } catch (error) {
                console.error(`Failed to refresh capacity for ${teamId}:`, error);
            }
        },
        [fetchTeamCapacity]
    );

    const refreshAll = useCallback(async () => {
        setIsRefreshing(true);
        await Promise.all(TEAM_IDS.map(id => refreshTeamCapacity(id)));
        setIsRefreshing(false);
    }, [refreshTeamCapacity]);

    useEffect(() => {
        TEAM_IDS.forEach(id => fetchTeamCapacity(id));
    }, [fetchTeamCapacity]);

    const getLoadColor = (loadPercent: number) => {
        if (loadPercent >= 90) return 'text-red-500';
        if (loadPercent >= 75) return 'text-[#8A9150]';
        return 'text-[#4267A1]';
    };

    const getAvailabilityColor = (availabilityPercent: number) => {
        if (availabilityPercent <= 10) return 'text-red-500';
        if (availabilityPercent <= 25) return 'text-[#8A9150]';
        return 'text-[#4267A1]';
    };

    const formatLastUpdated = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="geex-card p-8 premium-card-hover">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-gray-700" />
                    <h3
                        className="text-2xl font-bold text-gray-900"
                        style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                        Team Capacity
                    </h3>
                </div>
                <button
                    onClick={refreshAll}
                    disabled={isRefreshing}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                    <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh All
                </button>
            </div>

            <div className="space-y-6">
                {teams.map((team, teamIndex) => (
                    <motion.div
                        key={team.teamId}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: teamIndex * 0.1 }}
                        className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h4
                                    className="text-xl font-semibold text-gray-900 mb-1"
                                    style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                    {team.teamName}
                                </h4>
                                {team.surface && (
                                    <div className="text-xs text-gray-500">
                                        Last updated: {formatLastUpdated(team.surface.lastUpdated)}{' '}
                                        | Sample size: {team.surface.sampleSize} tasks
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => refreshTeamCapacity(team.teamId)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Refresh this team"
                            >
                                <RefreshCw
                                    className={`w-4 h-4 ${team.isLoading ? 'animate-spin' : ''}`}
                                />
                            </button>
                        </div>

                        {team.isLoading && !team.surface && (
                            <div className="flex items-center justify-center py-8 text-gray-500">
                                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                                Loading capacity data...
                            </div>
                        )}

                        {team.error && (
                            <div className="flex items-center justify-center py-8 text-gray-500">
                                <span className="text-sm">{team.error}</span>
                            </div>
                        )}

                        {!team.surface && !team.isLoading && !team.error && (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                                <span className="text-sm mb-2">No capacity data available</span>
                                <button
                                    onClick={() => refreshTeamCapacity(team.teamId)}
                                    className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                    Click to compute capacity
                                </button>
                            </div>
                        )}

                        {team.surface && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                                    <div className="text-sm text-gray-600 mb-1">Current Load</div>
                                    <div
                                        className={`text-2xl font-bold ${getLoadColor(team.surface.currentLoadPercent)}`}
                                    >
                                        {team.surface.currentLoadPercent}%
                                    </div>
                                </div>

                                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                                    <div className="text-sm text-gray-600 mb-1">Availability</div>
                                    <div
                                        className={`text-2xl font-bold ${getAvailabilityColor(team.surface.availabilityPercent)}`}
                                    >
                                        {team.surface.availabilityPercent}%
                                    </div>
                                </div>

                                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                                    <div className="text-sm text-gray-600 mb-1">Overload Risk</div>
                                    <div className="flex items-center gap-2">
                                        {team.surface.predictedOverload ? (
                                            <>
                                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                                <span className="text-lg font-semibold text-red-500">
                                                    High
                                                </span>
                                            </>
                                        ) : (
                                            <span className="text-lg font-semibold text-green-600">
                                                Low
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                                    <div className="text-sm text-gray-600 mb-1">Time Window</div>
                                    <div className="text-lg font-semibold text-gray-900">
                                        {team.surface.window === 'now'
                                            ? 'Current'
                                            : team.surface.window}
                                    </div>
                                </div>
                            </div>
                        )}
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default TeamCapacityPanel;
